import React from 'react';
import { Flag, Clock, Layers, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';

function fmtRelative(iso) {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '—';
  const seconds = Math.max(1, Math.round((Date.now() - t) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const m = Math.round(seconds / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}

function StatTile({ icon: Icon, label, value, sub, tone, onClick, title, active, loading }) {
  const Tag = onClick ? 'button' : 'div';
  const cls = [
    'fr-stat',
    `fr-stat--${tone || 'neutral'}`,
    onClick ? 'fr-stat--clickable' : '',
    active ? 'is-active' : '',
  ].filter(Boolean).join(' ');
  // Loader2 in place of the value while the query hasn't returned yet —
  // avoids a misleading "0" that reads as "no issues" instead of
  // "still loading".
  const showSpinner = loading && (value == null || value === '—');
  return (
    <Tag className={cls} onClick={onClick} title={title} type={onClick ? 'button' : undefined}>
      <Icon size={14} className="fr-stat__icon" />
      <div className="fr-stat__body">
        <div className="fr-stat__value">
          {showSpinner ? <Loader2 size={18} className="fr-spin" /> : value}
        </div>
        <div className="fr-stat__label">{label}</div>
        {sub && <div className="fr-stat__sub">{sub}</div>}
      </div>
    </Tag>
  );
}

function FlagStats({ stats, isLoading, error, sort, onDrillTo }) {
  if (error) {
    return (
      <div className="fr-stats fr-stats--error">
        <AlertCircle size={14} /> Stats failed: {error.message || String(error)}
      </div>
    );
  }
  // No longer return a "Loading stats…" text block while loading —
  // render the tiles in their loading state instead so the operator
  // can see the structure of the page immediately and gets a clear
  // "data syncing" signal (the per-tile spinner) instead of a vague
  // text label. Once data lands the spinners flip to numbers.
  const t = stats?.totals || {};
  const hasTotals = !!stats?.totals;
  const itemsValue = hasTotals ? (t.items_in_queue ?? 0).toLocaleString() : null;
  const last24Value = hasTotals ? (t.flags_24h ?? 0).toLocaleString() : null;
  const resolvedValue = hasTotals ? (t.resolved_7d ?? 0).toLocaleString() : null;
  const oldestValue = hasTotals ? fmtRelative(stats.oldest_open?.oldest) : null;
  return (
    <div className="fr-stats">
      <StatTile
        icon={Layers}
        label="Items in queue"
        value={itemsValue}
        sub={hasTotals ? `${(t.open_flags ?? 0).toLocaleString()} open flags` : null}
        tone="primary"
        onClick={() => onDrillTo({ kind: 'reset' })}
        title="Reset filters and sort to default (priority)"
        active={sort === 'priority'}
        loading={!hasTotals}
      />
      <StatTile
        icon={Flag}
        label="Last 24h"
        value={last24Value}
        sub={hasTotals ? `${(t.flags_7d ?? 0).toLocaleString()} in last 7d` : null}
        title="Recent-activity stats — drilldown to created-since views coming when the queue supports it"
        loading={!hasTotals}
      />
      <StatTile
        icon={CheckCircle2}
        label="Resolved · 7d"
        value={resolvedValue}
        sub="Operator-closed"
        tone="success"
        title="Resolved flags are kept for audit; a dedicated 'resolved view' is a future build (the queue endpoint only returns open flags today)"
        loading={!hasTotals}
      />
      <StatTile
        icon={Clock}
        label="Oldest open"
        value={oldestValue}
        sub={stats?.oldest_open?.item_id ? `Item #${stats.oldest_open.item_id}` : ''}
        onClick={() => onDrillTo({ kind: 'sort', value: 'age_desc' })}
        title="Sort the queue by age (oldest first)"
        active={sort === 'age_desc'}
        loading={!hasTotals}
      />
    </div>
  );
}

export default FlagStats;
